import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/landingPage';
import AuthPage from './pages/authPage';
import HomePage from './pages/home';
import CreateCvPage from './pages/createcv';
import CreateJobListingPage from './pages/createJobListing';
import InterviewPage from './pages/interviewPage';
import CandidatesPage from './pages/candidatesPage';
import EmailVerificationPage from './pages/emailVerificationPage';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUser(data.data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const getRedirectPath = (user) => {
    if (!user) return '/';
    if (user.isInterviewing) return '/interview';
    if (user.signUpFinished) return '/home';
    return user.role === 'job_seeker' ? '/createcv' : '/createjoblisting';
  };

  const ProtectedRoute = ({ children, requireSignUpFinished = false }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    if (requireSignUpFinished && !user.signUpFinished) {
      return <Navigate to={getRedirectPath(user)} replace />;
    }
    return children;
  };

  const SetupRoute = ({ children, allowedRole }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    if (user.role !== allowedRole) {
      return <Navigate to={getRedirectPath(user)} replace />;
    }
    return children;
  };

  const InterviewRoute = ({ children }) => {
    if (!user) {
      return <Navigate to="/" replace />;
    }
    // Check if there's interview data in session storage
    const interviewData = sessionStorage.getItem('interviewData');
    if (!interviewData && !user.isInterviewing) {
      return <Navigate to="/home" replace />;
    }
    return children;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%)',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(124, 152, 133, 0.2)',
          borderTopColor: '#7c9885',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <Routes>
      {/* Landing page - public */}
      <Route 
        path="/" 
        element={
          user ? <Navigate to={getRedirectPath(user)} replace /> : <LandingPage />
        } 
      />

      {/* Auth page - login/register */}
      <Route 
        path="/auth" 
        element={
          user ? <Navigate to={getRedirectPath(user)} replace /> : <AuthPage setUser={setUser} />
        } 
      />

      {/* Email verification page */}
      <Route 
        path="/verify-email" 
        element={<EmailVerificationPage setUser={setUser} />} 
      />
      
      <Route 
        path="/home" 
        element={
          <ProtectedRoute requireSignUpFinished>
            <HomePage user={user} setUser={setUser} />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/createcv" 
        element={
          <SetupRoute allowedRole="job_seeker">
            <CreateCvPage user={user} setUser={setUser} />
          </SetupRoute>
        } 
      />
      
      <Route 
        path="/createjoblisting" 
        element={
          <SetupRoute allowedRole="hiring_manager">
            <CreateJobListingPage user={user} setUser={setUser} />
          </SetupRoute>
        } 
      />

      <Route 
        path="/interview" 
        element={
          <InterviewRoute>
            <InterviewPage user={user} setUser={setUser} />
          </InterviewRoute>
        } 
      />

      <Route 
        path="/candidates" 
        element={
          <ProtectedRoute requireSignUpFinished>
            <CandidatesPage user={user} setUser={setUser} />
          </ProtectedRoute>
        } 
      />
      
      <Route path="*" element={<Navigate to={getRedirectPath(user)} replace />} />
    </Routes>
  );
}

export default App;